import { Stack, TableCellProps, Typography } from '@mui/material';
import { AttendanceCode } from '@schooly/api';
import { ATTENDANCE, buildTestId } from '@schooly/constants/test-ids';
import { FC, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  GridCell,
  GridContainer,
  GridHeaderCellContent,
  GridRowName,
} from '../../../components/uikit-components/Grid/Grid';
import { Table, TableCell } from '../../../components/uikit-components/Table/Table';
import {
  AttendanceAgeGroupSummary,
  getAttendancePercentages,
  getNoStatusTotal,
  getPresentAbsentCodeIds,
  getTotalByCodeIds,
} from '../attendance-utils';
import { JustificationCounters } from '../AttendanceJustificationCounters';
import { TypographyWithOverflowHint } from 'libs/style/src/components/TypographyWithOverflowHint';

interface AttendanceRegisterPreviewAgeGroupsGridProps {
  codes: AttendanceCode[];
  ageGroups: AttendanceAgeGroupSummary[];
}

const NUMBER_COL_WIDTH = 165;
const numberCellSx = { width: NUMBER_COL_WIDTH, minWidth: NUMBER_COL_WIDTH };

export const AttendanceRegisterPreviewAgeGroupsGrid: FC<
  AttendanceRegisterPreviewAgeGroupsGridProps
> = ({ codes, ageGroups }) => {
  const { present: presentCodeIds, absent: absentCodeIds } = useMemo(
    () => getPresentAbsentCodeIds(codes),
    [codes],
  );

  const columns: TableCell[] = useMemo(
    () => [
      {
        id: 'attendance-AgeGroup',
        hoverProps: { disableHover: true },
        renderContent: (props: TableCellProps) => (
          <GridCell sticky py={1.5} {...props}>
            <GridHeaderCellContent size="normal" color="common.grey">
              <FormattedMessage id="schoolProperty-AgeGroup" />
            </GridHeaderCellContent>
          </GridCell>
        ),
      },
      {
        id: 'attendance-Present',
        renderContent: (props: TableCellProps) => (
          <GridCell py={1.5} center sx={numberCellSx} {...props}>
            <GridHeaderCellContent size="normal" color="common.grey">
              <FormattedMessage id="attendance-Present" />
            </GridHeaderCellContent>
          </GridCell>
        ),
      },
      {
        id: 'attendance-Absent',
        renderContent: (props: TableCellProps) => (
          <GridCell py={1.5} center sx={numberCellSx} {...props}>
            <GridHeaderCellContent size="normal" color="common.grey">
              <FormattedMessage id="attendance-Absent" />
            </GridHeaderCellContent>
          </GridCell>
        ),
      },
      {
        id: 'attendance-NoStatus',
        renderContent: (props: TableCellProps) => (
          <GridCell py={1.5} center sx={numberCellSx} {...props}>
            <GridHeaderCellContent size="normal" color="common.grey">
              <FormattedMessage id="attendance-NoStatus" />
            </GridHeaderCellContent>
          </GridCell>
        ),
      },
    ],
    [],
  );

  const rows = useMemo(() => {
    if (!ageGroups.length) {
      return [];
    }

    return ageGroups.map((ageGroup) => {
      const noStatusCount = getNoStatusTotal(ageGroup.attendanceDetails);
      const presentCount = getTotalByCodeIds(presentCodeIds, ageGroup.attendanceDetails);
      const absentCount = getTotalByCodeIds(absentCodeIds, ageGroup.attendanceDetails);
      const { present: presentPercent, absent: absentPercent } = getAttendancePercentages(
        presentCount,
        absentCount,
      );

      const cellArray: TableCell[] = [
        {
          id: 'ageGroupName',
          renderContent: (props) => (
            <GridCell
              sticky
              noVerticalPadding
              py={1.25}
              sx={{ maxWidth: 200 }}
              {...props}
              data-test-id={buildTestId(ATTENDANCE.GROUP_CELL_PREFIX, ageGroup, 'name', 'id')}
            >
              <GridRowName>
                <TypographyWithOverflowHint variant="h3" noWrap>
                  {ageGroup.name}
                </TypographyWithOverflowHint>
              </GridRowName>
            </GridCell>
          ),
        },
        {
          id: 'attendance-Present',
          renderContent: (props: TableCellProps) => (
            <GridCell py={1.5} noHorizontalPadding center {...props}>
              <Stack direction="row" justifyContent="center" alignItems="center" width="100%">
                {presentCount ? (
                  <Typography color="text.primary">{presentPercent}%</Typography>
                ) : (
                  <Typography color="text.secondary">-</Typography>
                )}
              </Stack>
            </GridCell>
          ),
        },
        {
          id: 'attendance-Absent',
          renderContent: (props: TableCellProps) => (
            <GridCell py={1.5} noHorizontalPadding center {...props}>
              <Stack direction="row" justifyContent="center" alignItems="center" width="100%">
                {absentCount ? (
                  <Typography color="text.primary">{absentPercent}%</Typography>
                ) : (
                  <Typography color="text.secondary">-</Typography>
                )}
                <JustificationCounters counts={ageGroup.justificationCounts} />
              </Stack>
            </GridCell>
          ),
        },
        {
          id: 'attendance-NoStatus',
          renderContent: (props: TableCellProps) => (
            <GridCell py={1.5} noHorizontalPadding center {...props}>
              <Stack direction="row" justifyContent="center" alignItems="center" width="100%">
                {noStatusCount ? (
                  <Typography color="text.primary">{noStatusCount}</Typography>
                ) : (
                  <Typography color="text.secondary">-</Typography>
                )}
              </Stack>
            </GridCell>
          ),
        },
      ];

      return { id: ageGroup.id, cells: cellArray };
    });
  }, [ageGroups, presentCodeIds, absentCodeIds]);

  return (
    <GridContainer>
      <Table rows={rows} columns={columns} />
    </GridContainer>
  );
};
